import { zodResolver } from "@hookform/resolvers/zod";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import useSWR from "swr";
import { z } from "zod";
import {
  DestructiveButtonLargeV2,
  PrimaryButtonLargeV2,
} from "../../../../../../components/Buttons/Buttons";
import { SearchIcon, XIcon } from "../../../../../../components/Icons/Icons";
import { Row } from "../../../../../../components/Layout/Layout";
import { Notifications } from "../../../../../../components/Notifications/NotificationsContext";
import { TextField } from "../../../../../../components/TextFields/TextFields";
import {
  FormInputErrorText,
  H3DarkText,
} from "../../../../../../components/Typography/Typography";
import { endpoints } from "../../../../../../endpoints";
import { Form } from "../../../../../../layout/FormLayout";
import { useDebounce } from "../../../../../../util/hooks";
import { emailAddressRegex } from "../../../../../../util/regexes";
import { strings } from "../../../../../../util/strings";
import {
  makeUrlWithParams,
  TEMPORARY_HIGH_LIMIT,
  useFormWrapper,
  useStoreState,
} from "../../../../../../util/util";
import { zodRequiredString } from "../../../../../../util/zod.util";
import type { OptionType, User, UUID } from "../../../../../../types/types";
import { getVisibleTextLengthFromRichEditor } from "../util/AssetsUtil";
import { RichEditor } from "../../../../../../components/RichEditor/RichEditor";
import { Auth } from "../../../../../../components/Auth";

const ButtonContainerSideBySide = styled.div`
  display: grid;
  grid-template-columns: 50% 50%;
  grid-gap: 6px;
  margin-top: 20px;
`;

const SelectedListWrapper = styled.div`
  margin-top: 1rem;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const SelectedListItem = styled.div`
  display: flex;
  align-items: center;
  border: solid 1px ${({ theme }) => theme.primaryBorder};
  padding: 4px 8px;
  font-size: ${({ theme }) => theme.fontSizes.small};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  color: ${({ theme }) => theme.primaryTextColor};
  width: fit-content;
  gap: 8px;
  border-radius: 4px;

  span {
    text-wrap: nowrap;
  }
`;

const DropDownContainer = styled.div`
  box-shadow: 0px 6px 16px -6px ${({ theme }) => theme.shadowColor};
  display: block;
  overflow-y: auto;
  width: 100%;
  max-height: 195px;
  border: solid 1px ${({ theme }) => theme.primaryBorder};
  border-radius: 4px;
  button {
    display: flex;
    background-color: ${({ theme }) => theme.primaryBG};
    border: none;
    border-bottom: solid 1px ${({ theme }) => theme.primaryBorder};
    width: 100%;
    white-space: nowrap;
    padding: 16px 8px;
    cursor: pointer;
    font-size: ${({ theme }) => theme.fontSizes.small};
    font-weight: ${({ theme }) => theme.fontSizes.small};
    color: ${({ theme }) => theme.primaryTextColor};
  }

  button:focus {
    background-color: ${({ theme }) => theme.secondaryButtonBG};
    outline: none;
    color: ${({ theme }) => theme.primaryTextColor};
  }

  button:hover {
    background-color: ${({ theme }) => theme.secondaryButtonBG};
    color: ${({ theme }) => theme.primaryTextColor};
  }
`;

const SubHeading = styled.p`
  margin: 24px 0;
  font-size: ${({ theme }) => theme.fontSizes.medium};
`;

const RichEditorContainer = styled.div`
  .ql-container {
    height: fit-content;
  }
`;

type Option = { label: string; value: string };

type IShareAssets = {
  email: string;
  subject: string;
  body: string;
};

export type IShareAssetsData = {
  user_ids: string[];
  emails: string[];
  subject: string;
  body: string;
};

export const ShareAsset = ({
  handleShareAssetViaEmail,
  closeModal,
}: {
  handleShareAssetViaEmail: (data: IShareAssetsData) => Promise<void>;
  closeModal: () => void;
}) => {
  const { t } = useTranslation();
  const { notifyError } = useContext(Notifications);
  const { storefront_id } = useStoreState();

  const [searchQuery, setSearchQuery] = useState<string | undefined>("");
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [selectedList, setSelectedList] = useState<Option[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [displayOptions, setDisplayOptions] = useState<Option[]>([]);
  const [buyersOptions, setBuyersOptions] = useState<Option[]>([]);
  const [debouncedBuyerSearchQuery] = useDebounce(searchQuery, 300);
  const { user } = useContext(Auth);

  useSWR(
    makeUrlWithParams(endpoints.v1_storefronts_id_buyers(storefront_id), {
      limit: TEMPORARY_HIGH_LIMIT,
      q: debouncedBuyerSearchQuery,
    }),
    {
      onSuccess: (res) => {
        const buyersData = res?.data || [];
        setBuyersOptions(buyersData.map(convertUserToOptionUserID));
      },
      onError: (error) => {
        notifyError(t("There was an error fetching the buyers"), {
          error,
        });
      },
    }
  );

  const convertUserToOptionUserID = (user: User): OptionType<UUID> => {
    return {
      value: user.id,
      label: `${user.firstname} ${user.lastname} - ${user.email_address}`,
    };
  };

  const schema = z
    .object({
      email: z.string().optional(),
      subject: zodRequiredString(t),
      body: z
        .string()
        .refine((val) => getVisibleTextLengthFromRichEditor(val) > 0, {
          message: strings(t).thisIsARequiredField,
        }),
    })
    .superRefine((data, ctx) => {
      const email = data.email?.trim() || "";

      const hasSelected = selectedList?.length > 0;

      if (!hasSelected && email === "") {
        ctx.addIssue({
          path: ["email"],
          code: z.ZodIssueCode.custom,
          message: strings(t).thisIsARequiredField,
        });
      }

      if (email !== "") {
        const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
        if (!isValidEmail) {
          ctx.addIssue({
            path: ["email"],
            code: z.ZodIssueCode.custom,
            message: t("Invalid email ID"),
          });
        }
      }
    });

  const defaultBody = () => {
    return `${user?.firstname + " " + user?.lastname || "Unknown user"} from ${
      user?.company_name || "Unknown company"
    } shared assets with you.`;
  };

  const {
    handleSubmit,
    setValue,
    errors,
    formState,
    register,
    setError,
    clearErrors,
    watch,
  } = useFormWrapper({
    resolver: zodResolver(schema),
    defaultValues: {
      email: "",
      subject: `${user?.company_name} shared documents`,
      body: defaultBody(),
    },
  });

  const onSubmit = async (data: IShareAssets) => {
    const { userIds, emails } = extractRecipients(selectedList);

    const newData = {
      user_ids: userIds,
      emails,
      subject: data.subject,
      body: data.body,
    };

    setIsSubmitting(true);
    try {
      await handleShareAssetViaEmail(newData);
    } catch (error) {
      notifyError(t("Something went wrong while submitting the form"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const extractRecipients = (list: Option[]) => {
    const userIds = list.filter((item) => item.value).map((item) => item.value);
    const emails = list.filter((item) => !item.value).map((item) => item.label);

    return { userIds, emails };
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const trimmedValue = inputValue.trim();

    setSearchQuery(inputValue);
    setShowSearchModal(trimmedValue.length > 0);
    clearErrors("email");

    const customOption: Option = {
      label: `Enter "${trimmedValue}"`,
      value: "",
    };

    const combinedOptions = [customOption, ...buyersOptions];
    setDisplayOptions(combinedOptions);
  };

  const handleItemClick = (item: Option) => {
    let cleanLabel = item.label;

    if (cleanLabel.startsWith('Enter "') && cleanLabel.endsWith('"')) {
      cleanLabel = cleanLabel.slice(7, -1);
    }

    const hasDash = cleanLabel.includes(" - ");

    if (searchQuery !== "" && !emailAddressRegex.test(cleanLabel) && !hasDash) {
      setError("email", {
        type: "manual",
        message: t("Invalid email ID"),
      });
      return;
    }

    const hasAlreadyExist = selectedList.some(
      (list) => list.label.toLowerCase() === cleanLabel.toLowerCase()
    );

    if (!hasAlreadyExist) {
      clearErrors("email");
      setSelectedList((prev) => [...prev, { ...item, label: cleanLabel }]);
      setValue("email", "");
      setSearchQuery("");
      setShowSearchModal(false);
    } else {
      setError("email", {
        type: "manual",
        message: t("Email Id is already exist"),
      });
    }
  };

  const handleEmailAdd = () => {
    const input = searchQuery?.trim();
    if (!input) return;

    if (!emailAddressRegex.test(input)) {
      setError("email", {
        type: "manual",
        message: t("Invalid email ID"),
      });
      return;
    }

    const hasAlreadyExist = selectedList.some((item) =>
      input.includes(item.label)
    );

    if (!hasAlreadyExist) {
      clearErrors("email");
      setSelectedList((prev) => [...prev, { label: input, value: "" }]);
      setSearchQuery("");
      setValue("email", "");
      setShowSearchModal(false);
    } else {
      setError("email", {
        type: "manual",
        message: t("Email Id is already exist"),
      });
    }
  };

  const handleRemoveRecipient = (indexToRemove: number) => {
    const updatedList = selectedList.filter(
      (_, index) => index !== indexToRemove
    );
    setSelectedList(updatedList);
  };

  const handleInputFocus = () => {
    const trimmedValue = (searchQuery || "").trim();
    if (trimmedValue.length > 0) {
      setShowSearchModal(true);
    } else {
      setShowSearchModal(true);
      setDisplayOptions(buyersOptions);
    }
  };

  useEffect(() => {
    if (!searchQuery || searchQuery.trim() === "") {
      setDisplayOptions(buyersOptions);
      return;
    }

    const customOption = {
      label: `Enter "${searchQuery.trim()}"`,
      value: "",
    };

    setDisplayOptions([customOption, ...buyersOptions]);
  }, [buyersOptions, searchQuery]);

  return (
    <div>
      <H3DarkText>{t("Share asset via email")}</H3DarkText>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <SubHeading>{t("Search for recipients")}</SubHeading>

        <div style={{ marginBottom: 0 }}>
          <TextField
            name="email"
            label={t("Recipients")}
            theref={register({
              required: true,
            })}
            formState={formState}
            errors={errors}
            type="text"
            Icon={SearchIcon}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={() => {
              setTimeout(() => setShowSearchModal(false), 200);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleEmailAdd();
              }
            }}
          />
        </div>

        {showSearchModal && displayOptions.length > 0 && (
          <>
            <DropDownContainer>
              {displayOptions.map((item: Option) => {
                return (
                  <button
                    key={item.label}
                    onClick={() => handleItemClick(item)}
                    type="button"
                  >
                    {item.label}
                  </button>
                );
              })}
            </DropDownContainer>
          </>
        )}

        {selectedList && selectedList.length > 0 && (
          <SelectedListWrapper>
            {selectedList?.map((list, index) => {
              return (
                <SelectedListItem key={index}>
                  <span>{list.label}</span>
                  <Row onClick={() => handleRemoveRecipient(index)}>
                    <XIcon width={16} height={16} />
                  </Row>
                </SelectedListItem>
              );
            })}
          </SelectedListWrapper>
        )}

        <SubHeading>{t("Email Content")}</SubHeading>

        <TextField
          name="subject"
          label={t("Subject")}
          theref={register({
            required: true,
          })}
          formState={formState}
          errors={errors}
          type="text"
        />

        <RichEditorContainer>
          <RichEditor
            value={defaultBody()}
            name="body"
            label={t("Body")}
            useFormMethods={{
              errors,
              register,
              setValue,
              watch,
              formState,
              clearErrors,
            }}
          />
        </RichEditorContainer>
        {errors?.body && (
          <FormInputErrorText style={{ marginTop: "-15px" }}>
            {t(errors.body.message || "This field is required.")}
          </FormInputErrorText>
        )}

        <ButtonContainerSideBySide>
          <DestructiveButtonLargeV2 type="button" onClick={() => closeModal()}>
            {t("Cancel")}
          </DestructiveButtonLargeV2>
          <PrimaryButtonLargeV2 type="submit" loading={isSubmitting}>
            {t("Share")}
          </PrimaryButtonLargeV2>
        </ButtonContainerSideBySide>
      </Form>
    </div>
  );
};
